import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { Shield, Users, MapPin, Phone, Mail, FileCheck, BookOpen, Award, Clock, CheckCircle2 } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const AboutPage = () => {
  usePageSEO(
    "About PeptidesForMedSpas | Mission & Disclosures",
    "Learn about PeptidesForMedSpas LLC — a Hawaii-based wellness company focused on med spa research peptides, ingredient transparency, and peptide education."
  );

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">About Our Company</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#3a2a26] mb-6" data-testid="about-heading">
            About Peptides For Med Spas
          </h1>
          <p className="text-xl text-[#7a6b62] max-w-3xl mx-auto">
            A Hawaii-based wellness company built on transparency, education, and honest product information.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-[#3a2a26] mb-6">Our Story</h2>
            <div className="space-y-4 text-[#7a6b62] leading-relaxed">
              <p>
                PeptidesForMedSpas was founded in 2024 with a simple mission: provide informed consumers and med spa professionals 
                with access to wellness products and the educational resources they need to make informed decisions.
              </p>
              <p>
                We noticed that the peptide and peptide and wellness space was full of exaggerated claims, 
                unclear labeling, and misleading marketing. We set out to be different — offering transparent 
                ingredient information, honest product descriptions, and science-based educational content.
              </p>
              <p>
                Based in Honolulu, Hawaii, we operate as a registered LLC and take our responsibility to our 
                customers seriously. Every product on our site includes full ingredient lists, and our blog 
                features evidence-based articles reviewed for accuracy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#3a2a26] text-center mb-12">What We Stand For</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-[#fdf2f4] rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileCheck className="w-7 h-7 text-[#b89a6d]" />
              </div>
              <h3 className="text-lg font-bold text-[#3a2a26] mb-3">Ingredient Transparency</h3>
              <p className="text-[#7a6b62] text-sm">Every product includes full ingredient lists, directions for use, and applicable warnings. No hidden formulas or proprietary blends.</p>
            </div>
            <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-[#fdf2f4] rounded-xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-7 h-7 text-[#b89a6d]" />
              </div>
              <h3 className="text-lg font-bold text-[#3a2a26] mb-3">Education First</h3>
              <p className="text-[#7a6b62] text-sm">Our blog features 14 evidence-based articles on peptide science, safety, and recovery. We present research honestly — including its limitations.</p>
            </div>
            <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-[#fdf2f4] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[#b89a6d]" />
              </div>
              <h3 className="text-lg font-bold text-[#3a2a26] mb-3">No False Claims</h3>
              <p className="text-[#7a6b62] text-sm">We never claim our products treat, cure, or prevent any disease. We don't compare to prescription medications or guarantee specific outcomes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Verification */}
      <section className="py-16 bg-[#fffaf5]" data-testid="business-verification">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#3a2a26] text-center mb-12">Business Verification</h2>
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Registered Business</div>
                    <div className="text-[#7a6b62] text-sm">PeptidesForMedSpas LLC — State of Hawaii</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Established</div>
                    <div className="text-[#7a6b62] text-sm">Operating since 2024</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">SSL Secured</div>
                    <div className="text-[#7a6b62] text-sm">All transactions encrypted with SSL/TLS</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Transparent Policies</div>
                    <div className="text-[#7a6b62] text-sm">Published Privacy, Terms, Refund, Shipping, and Disclaimer policies</div>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Physical Address</div>
                    <div className="text-[#7a6b62] text-sm">Honolulu, HI 96815, United States</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Phone Support</div>
                    <div className="text-[#7a6b62] text-sm">+1 (808) 301-2134</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Email Support</div>
                    <div className="text-[#7a6b62] text-sm">everything@8eight8.net</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[#3a2a26] font-medium">Business Hours</div>
                    <div className="text-[#7a6b62] text-sm">Monday–Friday, 9 AM–5 PM HST</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Promise */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#3a2a26] text-center mb-12">Our Promise to You</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Award className="w-6 h-6 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#3a2a26] font-medium mb-1">30-Day Money-Back Guarantee</div>
                <p className="text-[#7a6b62] text-sm">Unopened products can be returned within 30 days for a full refund.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#3a2a26] font-medium mb-1">Secure Checkout</div>
                <p className="text-[#7a6b62] text-sm">Industry-standard SSL encryption protects all your personal information.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-6 h-6 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#3a2a26] font-medium mb-1">Real Customer Support</div>
                <p className="text-[#7a6b62] text-sm">Reach a real person by phone or email during business hours.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileCheck className="w-6 h-6 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#3a2a26] font-medium mb-1">Clear Labeling</div>
                <p className="text-[#7a6b62] text-sm">Every product shows full ingredients — no proprietary blends or hidden formulas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Ready to Explore?</h2>
          <p className="text-[#7a6b62] mb-8">Browse our products or read our educational articles.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="px-8 py-3 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-lg transition-colors"
              data-testid="about-shop-link"
            >
              View Products
            </Link>
            <Link
              to="/blog"
              className="px-8 py-3 bg-white border border-[#e8c5b8] hover:border-[#c4949c] text-[#b89a6d] font-semibold rounded-lg transition-colors"
              data-testid="about-blog-link"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
